// MUI Imports
import { useTheme } from '@mui/material/styles'

// Third-party Imports
import PerfectScrollbar from 'react-perfect-scrollbar'

// Type Imports
import type { VerticalMenuContextProps } from '@menu/components/vertical-menu/Menu'
import type { VerticalMenuDataType, VerticalMenuItemDataType, VerticalSubMenuDataType } from '@/types/menuTypes'

// Component Imports
import { Menu } from '@menu/vertical-menu'
import { GenerateVerticalMenu } from '@/components/GenerateMenu'

// Hook Imports
import useVerticalNav from '@menu/hooks/useVerticalNav'
import { usePermissions } from '@/hooks/usePermissions'

// Styled Component Imports
import StyledVerticalNavExpandIcon from '@menu/styles/vertical/StyledVerticalNavExpandIcon'

// Style Imports
import menuItemStyles from '@core/styles/vertical/menuItemStyles'
import menuSectionStyles from '@core/styles/vertical/menuSectionStyles'

// Util Imports
import { filterMenuByPermission } from '@/utils/permissions'
import { LIST_ROUTE_SUFFIX, MODULE_LABELS, MODULE_ROUTES, NAV_GROUP_LABELS } from '@/utils/constants/navigationLabels'

type RenderExpandIconProps = {
  open?: boolean
  transitionDuration?: VerticalMenuContextProps['transitionDuration']
}

type Props = {
  scrollMenu: (container: any, isPerfectScrollbar: boolean) => void
}

const RenderExpandIcon = ({ open, transitionDuration }: RenderExpandIconProps) => (
  <StyledVerticalNavExpandIcon open={open} transitionDuration={transitionDuration}>
    <i className='tabler-chevron-right' />
  </StyledVerticalNavExpandIcon>
)

// Ítems de navegación del admin. `permission` es opcional: los ítems sin permiso asociado siempre
// se muestran (comportamiento actual, sin regresión) hasta que se migre ese módulo (BEP-1114).
const getVerticalMenuData = (): VerticalMenuDataType[] => [
  { label: 'Inicio', href: '/admin', icon: 'tabler-smart-home' },
  {
    label: MODULE_LABELS.usuarios,
    href: `${MODULE_ROUTES.usuarios}${LIST_ROUTE_SUFFIX}`,
    icon: 'tabler-user-up',
    permission: 'user:read'
  },
  {
    label: MODULE_LABELS.puestos,
    href: `${MODULE_ROUTES.puestos}${LIST_ROUTE_SUFFIX}`,
    icon: 'tabler-briefcase',
    permission: 'job:read'
  },
  {
    label: MODULE_LABELS.empleados,
    href: `${MODULE_ROUTES.empleados}${LIST_ROUTE_SUFFIX}`,
    icon: 'tabler-user-down',
    permission: 'employee:read'
  },
  {
    label: NAV_GROUP_LABELS.security,
    icon: 'tabler-lock',
    children: [
      {
        label: MODULE_LABELS.permisos,
        href: `${MODULE_ROUTES.permisos}${LIST_ROUTE_SUFFIX}`,
        permission: 'permission:read'
      },
      { label: MODULE_LABELS.roles, href: `${MODULE_ROUTES.roles}${LIST_ROUTE_SUFFIX}`, permission: 'role:read' }
    ]
  },
  {
    label: MODULE_LABELS.tiendas,
    href: `${MODULE_ROUTES.tiendas}${LIST_ROUTE_SUFFIX}`,
    icon: 'tabler-building-store',
    permission: 'store:read'
  },
  {
    label: MODULE_LABELS['formas-de-pago'],
    href: `${MODULE_ROUTES['formas-de-pago']}${LIST_ROUTE_SUFFIX}`,
    icon: 'tabler-report-money',
    permission: 'payment-method:read'
  },
  {
    label: MODULE_LABELS['secuencias-numericas'],
    href: `${MODULE_ROUTES['secuencias-numericas']}${LIST_ROUTE_SUFFIX}`,
    icon: 'tabler-list-numbers',
    permission: 'sequence:read'
  },
  {
    label: NAV_GROUP_LABELS.products,
    icon: 'tabler-cube',
    children: [
      {
        label: MODULE_LABELS.productos,
        href: `${MODULE_ROUTES.productos}${LIST_ROUTE_SUFFIX}`,
        permission: 'product:read'
      },
      {
        label: MODULE_LABELS.surtidos,
        href: `${MODULE_ROUTES.surtidos}${LIST_ROUTE_SUFFIX}`,
        permission: 'assortment:read'
      },
      {
        label: MODULE_LABELS.categorias,
        href: `${MODULE_ROUTES.categorias}${LIST_ROUTE_SUFFIX}`,
        permission: 'category:read'
      },
      {
        label: MODULE_LABELS['grupo-de-precios'],
        href: `${MODULE_ROUTES['grupo-de-precios']}${LIST_ROUTE_SUFFIX}`,
        permission: 'price_group:read'
      },
      {
        label: MODULE_LABELS['niveles-de-precio'],
        href: `${MODULE_ROUTES['niveles-de-precio']}${LIST_ROUTE_SUFFIX}`,
        permission: 'price_group:read'
      }
    ]
  },
  {
    label: MODULE_LABELS.promociones,
    href: `${MODULE_ROUTES.promociones}${LIST_ROUTE_SUFFIX}`,
    icon: 'tabler-brand-producthunt',
    permission: 'promotion:read'
  },
  {
    label: NAV_GROUP_LABELS.digitalServices,
    icon: 'tabler-plug-connected',
    children: [
      {
        label: MODULE_LABELS.proveedores,
        href: `${MODULE_ROUTES.proveedores}${LIST_ROUTE_SUFFIX}`,
        permission: 'ds_provider:read'
      },
      {
        label: MODULE_LABELS.transacciones,
        href: `${MODULE_ROUTES.transacciones}${LIST_ROUTE_SUFFIX}`,
        permission: 'ds_transaction:read'
      }
    ]
  },
  {
    label: MODULE_LABELS.clientes,
    href: `${MODULE_ROUTES.clientes}${LIST_ROUTE_SUFFIX}`,
    icon: 'tabler-users-group',
    permission: 'customer:read'
  },
  { label: 'Acerca de', href: '/admin/about', icon: 'tabler-info-circle' }
]

// El ítem del listado queda activo, con su grupo abierto, también en el detalle, las pestañas y crear del módulo.
const matchByModule = (items: VerticalMenuDataType[]): VerticalMenuDataType[] =>
  items.map(item => {
    const subMenuItem = item as VerticalSubMenuDataType
    const menuItem = item as VerticalMenuItemDataType

    if (subMenuItem.children) return { ...subMenuItem, children: matchByModule(subMenuItem.children) }

    return menuItem.href?.endsWith(LIST_ROUTE_SUFFIX)
      ? { ...menuItem, exactMatch: false, activeUrl: menuItem.href.replace(LIST_ROUTE_SUFFIX, '') }
      : item
  })

const VerticalMenu = ({ scrollMenu }: Props) => {
  // Hooks
  const theme = useTheme()
  const verticalNavOptions = useVerticalNav()
  const { canAccess, status } = usePermissions()

  // Vars
  const { isBreakpointReached, transitionDuration } = verticalNavOptions

  const ScrollWrapper = isBreakpointReached ? 'div' : PerfectScrollbar

  const menuData = filterMenuByPermission(matchByModule(getVerticalMenuData()), canAccess)

  return (
    // eslint-disable-next-line lines-around-comment
    /* Custom scrollbar instead of browser scroll, remove if you want browser scroll only */
    <ScrollWrapper
      {...(isBreakpointReached
        ? {
            className: 'bs-full overflow-y-auto overflow-x-hidden',
            onScroll: container => scrollMenu(container, false)
          }
        : {
            options: { wheelPropagation: false, suppressScrollX: true },
            onScrollY: container => scrollMenu(container, true)
          })}
    >
      {/* Incase you also want to scroll NavHeader to scroll with Vertical Menu, remove NavHeader from above and paste it below this comment */}
      {/* Vertical Menu */}
      {/* El menú se dibuja al cargar la sesión: montado antes, el submenú activo no se abre. */}
      {status !== 'loading' && (
        <Menu
          popoutMenuOffset={{ mainAxis: 23 }}
          menuItemStyles={menuItemStyles(verticalNavOptions, theme)}
          renderExpandIcon={({ open }) => <RenderExpandIcon open={open} transitionDuration={transitionDuration} />}
          renderExpandedMenuItemIcon={{ icon: <i className='tabler-circle text-xs' /> }}
          menuSectionStyles={menuSectionStyles(verticalNavOptions, theme)}
        >
          <GenerateVerticalMenu menuData={menuData} />
        </Menu>
      )}
    </ScrollWrapper>
  )
}

export default VerticalMenu
